import { Controller } from "@hotwired/stimulus"

import * as Ga4 from "@/helpers/ga4_helper"
import { pluralize } from "@/helpers/pluralize"
import { formatPrice } from "@/helpers/price_format"

export default class extends Controller {
  static targets = [
    "footerTotalPrice",
    "paxInput",
    "priceBasis",
    "productList",
    "productRadio",
    "productText"
  ]
  declare readonly footerTotalPriceTarget: HTMLElement
  declare readonly paxInputTarget: HTMLInputElement
  declare readonly priceBasisTarget: HTMLElement
  declare readonly productListTargets: HTMLOListElement[]
  declare readonly productRadioTargets: HTMLInputElement[]
  declare readonly productTextTargets: HTMLInputElement[]

  static values = {
    ecommerceItem: Object
  }
  declare readonly ecommerceItemValue: Ga4.EcommerceItem

  connect(): void {
    this.setFooterContent()
  }

  selectPax(): void {
    Ga4.selectContentEvent(
      "Package Voucher Pax Options",
      this.paxInputTarget.value,
      this.ecommerceItemValue.item_name
    )

    // Show/hide the appropriate product options.
    this.productListTargets.forEach((list) => {
      list.hidden = !this.matchesSelectedPax(list)
    })

    // For the new pax, select the same product as the previous pax, or if unavailable then the
    // first product. We use `.checked = true` instead of `.click()` so that `#selectProduct` is
    // invoked without firing the GA4 event on the element which we don't want (since user hasn't
    // interacted with it).
    const currentSelectedProduct = this.selectedProductRadio.value
    const productToSelect =
      this.productRadiosForSelectedPax.find((radio) => radio.value === currentSelectedProduct) ||
      this.productRadiosForSelectedPax[0]
    productToSelect.checked = true
    this.setFooterContent()
  }

  selectProduct(): void {
    this.setFooterContent()
  }

  submitForm(): void {
    Ga4.addToCartEvent(
      {
        ...this.ecommerceItemValue,
        item_category3: this.paxInputTarget.value,
        price: this.selectedProductPrice,
        quantity: 1
      },
      this.selectedProductPrice
    )
  }

  private matchesSelectedPax(element: HTMLElement): boolean {
    return element.dataset.pax! === this.paxInputTarget.value
  }

  private get productRadiosForSelectedPax(): HTMLInputElement[] {
    return this.productRadioTargets.filter(this.matchesSelectedPax.bind(this))
  }

  private get selectedProductPrice(): number {
    return +this.selectedProductRadio.dataset.price!
  }

  private get selectedProductRadio(): HTMLInputElement {
    return this.productRadioTargets.find((product) => product.checked)!
  }

  private setFooterContent(): void {
    this.footerTotalPriceTarget.textContent = formatPrice(this.selectedProductPrice)
    this.priceBasisTarget.textContent = `for ${pluralize(+this.paxInputTarget.value, "guest")}`
  }
}
